import { createBox3D, type Box3D } from 'box3d-web';
import type { Vec3 } from '../../services/fracture/voronoiFracture';
import { DestructionSceneRuntime, type DestructionSceneEventEnvelope } from '../destructionSceneRuntime';
import { createDestructionIdentityFactory } from '../identities';
import { SharedDestructionWorld } from '../sharedDestructionWorld';
import { createSiegeStructureAdapter, type HostedSiegeStructureAdapter, type SiegeFireCommand } from './siegeStructureAdapter';
import { SIEGE_FIXED_DT, SIEGE_SUBSTEPS, type SiegeFanout } from '../../structures/siege/siegePhysics';

export interface SiegePageSession {
  readonly shared: SharedDestructionWorld;
  readonly scene: DestructionSceneRuntime<SiegeFanout>;
  readonly adapter: HostedSiegeStructureAdapter;
  readonly runtime: HostedSiegeStructureAdapter['runtime'];
  readonly structureKey: string;
  advance(delta: number): number;
  drainEvents(): readonly DestructionSceneEventEnvelope<SiegeFanout>[];
  fireAt(origin: Vec3, direction: Vec3, charge: number): void;
  reset(): void;
  dispose(): void;
}

class RetainedSiegePageSession implements SiegePageSession {
  private disposed = false;
  private readonly box3d: Box3D;
  readonly shared: SharedDestructionWorld;
  readonly scene: DestructionSceneRuntime<SiegeFanout>;
  readonly adapter: HostedSiegeStructureAdapter;

  private constructor(box3d: Box3D, shared: SharedDestructionWorld, scene: DestructionSceneRuntime<SiegeFanout>, adapter: HostedSiegeStructureAdapter) {
    this.box3d = box3d;
    this.shared = shared;
    this.scene = scene;
    this.adapter = adapter;
  }

  static async create(): Promise<RetainedSiegePageSession> {
    const box3d = await createBox3D();
    const ids = createDestructionIdentityFactory({ namespace: 'siege-page', generation: 0 });
    const worldId = ids.world(0);
    const shared = new SharedDestructionWorld(box3d, worldId, { gravity: [0, -9.8, 0] });
    try {
      const ground = shared.ensureGround();
      const structure = ids.structure(0, worldId);
      const scene = new DestructionSceneRuntime<SiegeFanout>(shared, { fixedDt: SIEGE_FIXED_DT, substeps: SIEGE_SUBSTEPS });
      const adapter = await createSiegeStructureAdapter({ shared, structureId: structure, groundBody: ground.body });
      const session = new RetainedSiegePageSession(box3d, shared, scene, adapter);
      scene.attach(adapter);
      return session;
    } catch (error) {
      try { shared.dispose(); } catch { /* preserve construction failure */ }
      try { box3d.dispose(); } catch { /* preserve construction failure */ }
      throw error;
    }
  }

  get runtime() { return this.adapter.runtime; }
  get structureKey() { return this.adapter.structureKey; }
  advance(delta: number): number { this.assertLive(); return this.scene.advance(this.runtime.slowMo ? delta * 0.25 : delta); }
  drainEvents() { this.assertLive(); return this.scene.drainEvents(); }
  fireAt(origin: Vec3, direction: Vec3, charge: number): void {
    this.assertLive();
    const payload: SiegeFireCommand = {
      origin: { x: origin.x, y: origin.y, z: origin.z },
      direction: { x: direction.x, y: direction.y, z: direction.z },
      charge,
    };
    this.scene.enqueueCommand({ targetStructureKey: this.structureKey, type: 'siege-fire', payload });
  }
  reset(): void { this.assertLive(); this.scene.enqueueCommand({ targetStructureKey: this.structureKey, type: 'reset-structure', payload: {} }); }
  dispose(): void {
    if (this.disposed) return;
    let firstError: unknown;
    const attempt = (operation: () => void) => {
      try { operation(); } catch (error) { if (firstError === undefined) firstError = error; }
    };
    attempt(() => this.scene.dispose());
    attempt(() => this.adapter.dispose());
    attempt(() => this.shared.dispose());
    attempt(() => this.box3d.dispose());
    this.disposed = true;
    if (retained === this) retained = null;
    if (firstError !== undefined) throw firstError;
  }
  private assertLive(): void { if (this.disposed) throw new Error('Siege page session used after dispose()'); }
}

let retained: RetainedSiegePageSession | null = null;
let pending: Promise<RetainedSiegePageSession> | null = null;

export async function getSiegePageSession(): Promise<SiegePageSession> {
  if (retained && !pending) return retained;
  if (!pending) pending = RetainedSiegePageSession.create().then((value) => { retained = value; return value; }).finally(() => { pending = null; });
  return pending;
}

export function disposeExistingSiegePageSession(): void { retained?.dispose(); retained = null; pending = null; }
